import React, { Dispatch, SetStateAction } from 'react';
import Select from 'react-select';
import { Card, CardBody, Form, Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import styles from '../../styles/archiveui.module.css';
import { DocumentAuditAction } from '../../types/audit';
import { User } from '../../types/user';

interface SearchBarProps {
  updateActorFilter: Dispatch<SetStateAction<string>>;
  updateTargetFilter: Dispatch<SetStateAction<string>>;
  updateActionFilter: Dispatch<SetStateAction<string>>;
  updateStartDate: Dispatch<SetStateAction<string>>;
  updateEndDate: Dispatch<SetStateAction<string>>;
  targetFilter: string;
  actionFilter: string;
  startDate: string;
  endDate: string;
  searchAudits: () => void;
  users: User[];
  exportAudits: () => void;
}

const SearchBar = ({
  updateActorFilter,
  updateTargetFilter,
  updateActionFilter,
  updateStartDate,
  updateEndDate,
  targetFilter,
  actionFilter,
  startDate,
  endDate,
  searchAudits,
  users,
  exportAudits,
}: SearchBarProps): JSX.Element => {
  const [selectedUser, setSelectedUser] = React.useState<{ label: string; value: string } | null>(null);

  const userOptions = users.map((u) => ({
    label: `${u.email}${u.givenName || u.familyName ? ` — ${u.givenName ?? ''} ${u.familyName ?? ''}`.trimEnd() : ''}`,
    value: u.cognitoIds?.[0] ?? u.email,
  }));

  const handleUserChange = (option: { label: string; value: string } | null) => {
    setSelectedUser(option);
    updateActorFilter(option?.value ?? '');
  };

  const clearFilters = () => {
    updateActorFilter('');
    setSelectedUser(null);
    updateTargetFilter('');
    updateActionFilter('');
    updateStartDate('');
    updateEndDate('');
  };

  return (
    <div>
      <Card className={styles.filtersCard} style={{ marginTop: '1em', borderRadius: '.5em', paddingTop: '0em' }}>
        <CardBody className={styles.searchBarHeader}>
          <h5 style={{ textAlign: 'start', fontWeight: 500, marginTop: '.5em' }}>Audit Log</h5>
        </CardBody>
        <CardBody className={styles.searchBarBody}>
          <Form>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label for="userEmail">User</Label>
                  <Select
                    inputId="userEmail"
                    placeholder="Filter by user"
                    aria-label="User filter"
                    isClearable
                    options={userOptions}
                    value={selectedUser}
                    onChange={handleUserChange}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="documentId">Document ID</Label>
                  <Input
                    type="text"
                    name="documentId"
                    id="documentId"
                    placeholder="Filter by Document GUID"
                    aria-label="Document ID filter"
                    value={targetFilter}
                    onChange={(e) => updateTargetFilter(e.target.value.trim())}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label for="action">Action</Label>
                  <Input
                    type="select"
                    name="action"
                    id="action"
                    aria-label="Select Action"
                    value={actionFilter}
                    onChange={(e) => updateActionFilter(e.target.value)}
                  >
                    <option value="">&lt;All&gt;</option>
                    <option value={DocumentAuditAction.DOCUMENT_UPLOADED}>Document Uploaded</option>
                    <option value={DocumentAuditAction.DOCUMENT_CHANGED}>Document Changed</option>
                    <option value={DocumentAuditAction.DOCUMENT_ANNOTATED}>Document Annotated</option>
                    <option value={DocumentAuditAction.DOCUMENT_PURGED}>Document Purged</option>
                    <option value={DocumentAuditAction.DOCUMENT_TYPE_CHANGED}>Document Type Changed</option>
                    <option value={DocumentAuditAction.DOCUMENT_STATUS_CHANGED}>Document Status Changed</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="start-date">Start Date</Label>
                  <Input
                    type="date"
                    name="start-date"
                    id="start-date"
                    aria-label="Select Start Date"
                    value={startDate}
                    onChange={(e) => updateStartDate(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="end-date">End Date</Label>
                  <Input
                    type="date"
                    name="end-date"
                    id="end-date"
                    aria-label="Select End Date"
                    value={endDate}
                    onChange={(e) => updateEndDate(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <div className={styles.detailBtnContainer}>
              <Button id="clearFilterBtn" aria-label="Clear Filter Button" className={styles.scrbBtnBlueMargin} onClick={clearFilters}>
                Clear Filters
              </Button>
              <Button id="applyFilterBtn" aria-label="Apply Filter Button" className={styles.scrbBtnOrange} onClick={searchAudits}>
                Apply Filters
              </Button>
              <Button id="exportCsvBtn" aria-label="Export CSV Button" className={styles.scrbBtnBlueMargin} onClick={exportAudits}>
                Export CSV
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default SearchBar;
